import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { customFilterOption } from 'src/components/utilities/utils';
import {
    ENABLE_ROW_OPTIONS,
    Option,
    PREDEFINED_ROWS,
    RowType,
    SORT_OPTIONS,
    SPOTLIGHT_ROW_MODAL_ID,
    TYPE_META,
    getRowType
} from './spotlightRows.constants';

export interface SpotlightRowModalProps {
    mode: 'add' | 'edit';
    editingRow?: any;
    collectionOptions: Option[];
    categoryOptions: Option[];
    channelOptions: Option[];
    activePredefinedIds: string[];
    openKey: number;
    onSave: (payload: {
        type: RowType;
        contentId: string;
        sortOrder?: string;
        contentLimit?: number;
    }) => void;
}

const SpotlightRowModal = ({
    mode,
    editingRow,
    collectionOptions,
    categoryOptions,
    channelOptions,
    activePredefinedIds,
    openKey,
    onSave
}: SpotlightRowModalProps) => {
    const closeRef = useRef<HTMLButtonElement>(null);
    const initialType: RowType = editingRow ? getRowType(editingRow) : 'predefined';
    const [type, setType] = useState<RowType>(initialType);
    const [source, setSource] = useState<Option | null>(null);
    const [sortOrder, setSortOrder] = useState<Option>(SORT_OPTIONS[0]);
    const [contentLimit, setContentLimit] = useState<number>(8);
    const [error, setError] = useState('');

    const predefinedOptions: Option[] = PREDEFINED_ROWS.map((r) => ({
        value: r.id,
        label: r.label,
        isDisabled: activePredefinedIds.includes(r.id)
    })).sort((a, b) => a.label.localeCompare(b.label));

    const optionsFor = (t: RowType): Option[] =>
        t === 'predefined'
            ? predefinedOptions
            : t === 'collection'
                ? collectionOptions
                : t === 'category'
                    ? categoryOptions
                    : channelOptions;

    const sourceOptions: Option[] = optionsFor(type);

    useEffect(() => {
        const startType: RowType = editingRow ? getRowType(editingRow) : 'predefined';
        setType(startType);
        setError('');
        setSortOrder(SORT_OPTIONS[0]);
        setContentLimit(8);
        if (mode === 'edit' && editingRow) {

            setSource(optionsFor(startType).find((o) => o.value === String(editingRow.id)) || null);
        } else {
            setSource(null);
        }
    }, [openKey]);

    const handleTypeChange = (next: RowType) => {
        if (mode === 'edit') return;
        setType(next);
        setSource(null);
        setError('');
    };

    const submit = () => {
        if (!source) {
            setError(`Choose a ${type === 'predefined' ? 'pre-defined row' : type} to continue.`);
            return;
        }
        onSave({
            type,
            contentId: source.value,
            sortOrder: ENABLE_ROW_OPTIONS && type === 'channel' ? sortOrder.value : undefined,
            contentLimit: ENABLE_ROW_OPTIONS && type === 'category' ? contentLimit : undefined
        });
        closeRef.current?.click();
    };

    const sourceLabel =
        type === 'predefined'
            ? 'Pre-defined Row'
            : type === 'collection'
                ? 'Collection'
                : type === 'category'
                    ? 'Category'
                    : 'Channel';

    const predefinedHelp = PREDEFINED_ROWS.find((r) => r.id === source?.value)?.description;

    return (
        <div
            className="modal fade fixed top-0 left-0 hidden w-full h-full outline-none overflow-x-hidden overflow-y-auto modal-open"
            id={SPOTLIGHT_ROW_MODAL_ID}
            aria-labelledby={`${SPOTLIGHT_ROW_MODAL_ID}Label`}
            aria-hidden="true">
            <div className="modal-dialog relative w-auto pointer-events-none max-w-2xl">
                <div className="modal-content border-none shadow-lg relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current">
                    <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 pb-2 rounded-t-md">
                        <h5
                            className="text-xl font-semibold leading-normal text-center w-full"
                            id={`${SPOTLIGHT_ROW_MODAL_ID}Label`}>
                            {mode === 'add' ? 'Add' : 'Edit'} Row
                        </h5>
                        <button
                            ref={closeRef}
                            type="button"
                            className="dismiss-btn"
                            data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>

                    <div className="modal-body relative py-4 px-10">
                        {/* Row type cards */}
                        <div className="font-semibold mb-2">Row Type</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {(Object.keys(TYPE_META) as RowType[]).map((t) => {
                                const selected = type === t;
                                const locked = mode === 'edit' && !selected;
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        disabled={locked}
                                        onClick={() => handleTypeChange(t)}
                                        style={{
                                            textAlign: 'center',
                                            padding: '12px 8px',
                                            borderRadius: 10,
                                            border: `2px solid ${selected ? '#6d3df2' : '#e5e7eb'}`,
                                            background: selected ? '#f3efff' : '#fff',
                                            opacity: locked ? 0.45 : 1,
                                            cursor: locked ? 'not-allowed' : 'pointer'
                                        }}>
                                        <i className={`fa ${TYPE_META[t].icon}`} style={{ fontSize: 20, color: '#6d3df2' }}></i>
                                        <div className="font-semibold mt-1 text-sm">{TYPE_META[t].label}</div>
                                        <div className="text-xs text-gray-500 mt-1">{TYPE_META[t].hint}</div>
                                    </button>
                                );
                            })}
                        </div>
                        {mode === 'edit' && (
                            <p className="text-xs text-gray-500 mt-2 mb-0">
                                {`Row type can't be changed. You can pick a different {type} to show in this row.`}
                            </p>
                        )}


                        <div className="mt-5">
                            <label className="block font-semibold mb-1">{sourceLabel}</label>
                            <Select
                                options={sourceOptions}
                                maxMenuHeight={460}
                                value={source}
                                filterOption={customFilterOption}
                                placeholder={`Select ${sourceLabel.toLowerCase()}…`}
                                onChange={(val: any) => {
                                    setSource(val);
                                    setError('');
                                }}
                            />
                            {type === 'predefined' && predefinedHelp && (
                                <p className="text-xs text-gray-500 mt-1 mb-0">{predefinedHelp}</p>
                            )}
                        </div>


                        {ENABLE_ROW_OPTIONS && type === 'channel' && (
                            <div className="mt-4">
                                <label className="block font-semibold mb-1">Sort order</label>
                                <Select
                                    options={SORT_OPTIONS}
                                    value={sortOrder}
                                    isSearchable={false}
                                    onChange={(val: any) => setSortOrder(val)}
                                />
                                <p className="text-xs text-gray-500 mt-1 mb-0">Controls the order of content within this row.</p>
                            </div>
                        )}


                        {ENABLE_ROW_OPTIONS && type === 'category' && (
                            <div className="mt-4">
                                <label className="block font-semibold mb-1" htmlFor="contentLimit">
                                    Content limit
                                </label>
                                <input
                                    id="contentLimit"
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={contentLimit}
                                    onChange={(e) => setContentLimit(Math.max(1, Number(e.target.value) || 1))}
                                    className="border rounded-lg px-3"
                                    style={{ height: 38, width: 120 }}
                                />
                                <p className="text-xs text-gray-500 mt-1 mb-0">Number of items to display in the row.</p>
                            </div>
                        )}

                        {error && <p className="text-sm mt-3 mb-0" style={{ color: '#dc2626' }}>{error}</p>}
                    </div>

                    <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-center p-4 rounded-b-md">
                        <button
                            type="button"
                            onClick={submit}
                            className="px-6 btn-primary w-32 mr-2 ml-0 mt-0">
                            {mode === 'add' ? 'Add Row' : 'Save Row'}
                        </button>
                        <button
                            type="button"
                            className="mx-0 px-6 btn-primary discard-btn w-28 mt-0"
                            data-bs-dismiss="modal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpotlightRowModal;